import React, { useState, useEffect, useCallback } from 'react';
import Icon from 'components/AppIcon';
import { useNotifications } from 'hooks/useNotifications';
import Notifier from 'components/ui/Notifier';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';

const ClabBulkVerification = () => {
  const [step, setStep] = useState('upload'); // 'upload' | 'review' | 'complete'
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [verificationData, setVerificationData] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [reviewedIds, setReviewedIds] = useState(new Set());
  const [approvedIds, setApprovedIds] = useState(new Set());
  const [rejectedIds, setRejectedIds] = useState(new Set());
  const [flaggedIds, setFlaggedIds] = useState(new Set());
  const [showDocumentPanel, setShowDocumentPanel] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [approvalMode, setApprovalMode] = useState('batch'); // 'instant' | 'batch'

  const { showSuccess, showError, showInfo } = useNotifications();

  // Get current record
  const currentRecord = verificationData?.matched_records?.[currentIndex] || null;

  // Handle file selection
  const handleFileSelect = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      if (!selectedFile.name.match(/\.(xlsx|xls)$/i)) {
        showError('Please select an Excel file (.xlsx or .xls)');
        return;
      }
      setFile(selectedFile);
    }
  };

  // Upload Excel file
  const handleUpload = async () => {
    if (!file) {
      showError('Please select a file first');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/clab/bulk-verification/upload`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`
        },
        body: formData
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Upload failed');
      }

      setVerificationData(result.data);
      setStep('review');
      showSuccess(`Excel uploaded! ${result.data.matched_count} biodata matched`);
    } catch (error) {
      console.error('Upload error:', error);
      showError(error.message || 'Failed to upload Excel file');
    } finally {
      setUploading(false);
    }
  };

  // Navigation
  const goToNext = () => {
    if (currentIndex < (verificationData?.matched_records?.length || 0) - 1) {
      setCurrentIndex(currentIndex + 1);
      setShowDocumentPanel(false);
    }
  };

  const goToPrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      setShowDocumentPanel(false);
    }
  };

  // Actions
  const handleApprove = async () => {
    if (!currentRecord) return;

    if (approvalMode === 'instant') {
      // Instant approval - call API immediately
      setProcessing(true);
      try {
        const token = localStorage.getItem('authToken');
        const response = await fetch(`${API_BASE_URL}/clab/bulk-verification/approve-single`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            biodata_id: currentRecord.biodata_id,
            notes: 'Approved via bulk verification (instant mode)'
          })
        });

        const result = await response.json();

        if (!response.ok) {
          throw new Error(result.error || 'Approval failed');
        }

        // Track as approved
        const newApproved = new Set(approvedIds);
        newApproved.add(currentRecord.biodata_id);
        setApprovedIds(newApproved);

        const newReviewed = new Set(reviewedIds);
        newReviewed.add(currentRecord.biodata_id);
        setReviewedIds(newReviewed);

        // Remove from rejected/flagged
        const newRejected = new Set(rejectedIds);
        newRejected.delete(currentRecord.biodata_id);
        setRejectedIds(newRejected);

        const newFlagged = new Set(flaggedIds);
        newFlagged.delete(currentRecord.biodata_id);
        setFlaggedIds(newFlagged);

        showSuccess('Biodata approved and saved!');
        goToNext();
      } catch (error) {
        console.error('Instant approve error:', error);
        showError(error.message || 'Failed to approve biodata');
      } finally {
        setProcessing(false);
      }
    } else {
      // Batch mode - just mark for later submission
      const newApproved = new Set(approvedIds);
      newApproved.add(currentRecord.biodata_id);
      setApprovedIds(newApproved);

      const newReviewed = new Set(reviewedIds);
      newReviewed.add(currentRecord.biodata_id);
      setReviewedIds(newReviewed);

      // Remove from rejected/flagged if previously marked
      const newRejected = new Set(rejectedIds);
      newRejected.delete(currentRecord.biodata_id);
      setRejectedIds(newRejected);

      const newFlagged = new Set(flaggedIds);
      newFlagged.delete(currentRecord.biodata_id);
      setFlaggedIds(newFlagged);

      showInfo('Marked as approved');
      goToNext();
    }
  };

  const handleReject = () => {
    if (!currentRecord) return;

    const newRejected = new Set(rejectedIds);
    newRejected.add(currentRecord.biodata_id);
    setRejectedIds(newRejected);

    const newReviewed = new Set(reviewedIds);
    newReviewed.add(currentRecord.biodata_id);
    setReviewedIds(newReviewed);

    // Remove from approved/flagged
    const newApproved = new Set(approvedIds);
    newApproved.delete(currentRecord.biodata_id);
    setApprovedIds(newApproved);

    const newFlagged = new Set(flaggedIds);
    newFlagged.delete(currentRecord.biodata_id);
    setFlaggedIds(newFlagged);

    showInfo('Marked as rejected');
    goToNext();
  };

  const handleFlag = () => {
    if (!currentRecord) return;

    const newFlagged = new Set(flaggedIds);
    newFlagged.add(currentRecord.biodata_id);
    setFlaggedIds(newFlagged);

    const newReviewed = new Set(reviewedIds);
    newReviewed.add(currentRecord.biodata_id);
    setReviewedIds(newReviewed);

    showInfo('Flagged for later review');
    goToNext();
  };

  // Bulk submit approvals
  const handleBulkSubmit = async () => {
    if (approvedIds.size === 0) {
      showError('No biodata marked for approval');
      return;
    }

    setProcessing(true);
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/clab/bulk-verification/approve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          biodata_ids: Array.from(approvedIds),
          notes: 'Bulk approved via Excel verification'
        })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Bulk approval failed');
      }

      showSuccess(`Successfully approved ${result.data.success_count} biodata records!`);
      setStep('complete');
    } catch (error) {
      console.error('Bulk approve error:', error);
      showError(error.message || 'Failed to submit approvals');
    } finally {
      setProcessing(false);
    }
  };

  // Skip to completion (for instant mode when done reviewing)
  const handleFinishReview = () => {
    if (approvedIds.size === 0 && rejectedIds.size === 0 && flaggedIds.size === 0) {
      showError('Please review at least one biodata record');
      return;
    }
    setStep('complete');
  };

  // View document
  const viewDocument = (doc) => {
    setSelectedDocument(doc);
    setShowDocumentPanel(true);
  };

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (event) => {
      if (step !== 'review') return;

      switch (event.key.toLowerCase()) {
        case 'arrowright':
          goToNext();
          break;
        case 'arrowleft':
          goToPrevious();
          break;
        case 'a':
          if (!showDocumentPanel) handleApprove();
          break;
        case 'r':
          if (!showDocumentPanel) handleReject();
          break;
        case 'f':
          if (!showDocumentPanel) handleFlag();
          break;
        case 'escape':
          setShowDocumentPanel(false);
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [step, currentIndex, showDocumentPanel]);

  // Render upload screen
  if (step === 'upload') {
    return (
      <div className="min-h-screen bg-background p-8">
        <Notifier />
        <div className="max-w-4xl mx-auto">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-text-primary mb-2">
              Bulk Biodata Verification
            </h1>
            <p className="text-text-secondary">
              Upload an Excel file to quickly verify and approve multiple biodata records
            </p>
          </div>

          <div className="card p-8">
            <div className="mb-6">
              <h2 className="text-xl font-semibold text-text-primary mb-4">
                Upload Master List (Excel)
              </h2>
              <p className="text-sm text-text-secondary mb-4">
                Excel should contain: Name, Passport Number, DOB, Age, TEST (Pass/Fail), Remarks
              </p>

              <div className="border-2 border-dashed border-secondary-300 rounded-lg p-8 text-center">
                <Icon name="Upload" size={48} className="mx-auto text-secondary-400 mb-4" />

                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleFileSelect}
                  className="hidden"
                  id="excel-upload"
                />

                {!file ? (
                  <>
                    <label
                      htmlFor="excel-upload"
                      className="inline-block px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary-dark cursor-pointer transition-colors"
                    >
                      Select Excel File
                    </label>
                    <p className="text-sm text-text-tertiary mt-2">
                      .xlsx or .xls files only
                    </p>
                  </>
                ) : (
                  <div className="space-y-4">
                    <div className="flex items-center justify-center space-x-3 text-success">
                      <Icon name="CheckCircle" size={24} />
                      <span className="font-medium">{file.name}</span>
                    </div>
                    <div className="flex items-center justify-center space-x-3">
                      <label
                        htmlFor="excel-upload"
                        className="px-4 py-2 text-secondary-700 bg-surface border border-secondary-300 rounded-md hover:bg-secondary-50 cursor-pointer"
                      >
                        Change File
                      </label>
                      <button
                        onClick={handleUpload}
                        disabled={uploading}
                        className="px-6 py-2 bg-primary text-white rounded-md hover:bg-primary-dark disabled:opacity-50 flex items-center space-x-2"
                      >
                        {uploading && <Icon name="Loader2" size={16} className="animate-spin" />}
                        <span>{uploading ? 'Uploading...' : 'Upload & Match'}</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="bg-info-50 border border-info-200 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <Icon name="Info" size={20} className="text-info-600 mt-0.5" />
                <div className="text-sm text-info-800">
                  <p className="font-medium mb-2">How it works:</p>
                  <ol className="list-decimal list-inside space-y-1">
                    <li>System will match biodata by passport number</li>
                    <li>Review each match side-by-side with documents</li>
                    <li>Approve, reject, or flag for later review</li>
                    <li>Submit bulk approvals with one click</li>
                  </ol>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Render review screen
  if (step === 'review' && currentRecord) {
    const progress = ((currentIndex + 1) / verificationData.matched_records.length) * 100;
    const reviewedCount = reviewedIds.size;
    const totalRecords = verificationData.matched_records.length;

    return (
      <div className="min-h-screen bg-background">
        <Notifier />

        {/* Header with Progress */}
        <div className="bg-surface border-b border-secondary-200 p-4">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h1 className="text-2xl font-bold text-text-primary">
                  Bulk Verification Review
                </h1>
                <p className="text-sm text-text-secondary">
                  Record {currentIndex + 1} of {totalRecords} • {reviewedCount} reviewed
                </p>
              </div>

              <div className="flex items-center space-x-3">
                {/* Approval Mode Toggle */}
                <div className="flex flex-col items-end mr-4">
                  <p className="text-xs text-text-tertiary mb-1">Approval Mode:</p>
                  <div className="inline-flex rounded-md shadow-sm" role="group">
                    <button
                      type="button"
                      onClick={() => setApprovalMode('batch')}
                      className={`px-4 py-2 text-sm font-medium rounded-l-md border transition-colors ${
                        approvalMode === 'batch'
                          ? 'bg-primary text-white border-primary'
                          : 'bg-surface text-secondary-700 border-secondary-300 hover:bg-secondary-50'
                      }`}
                    >
                      <span className="flex items-center space-x-1">
                        <Icon name="Package" size={16} />
                        <span>Batch Mode</span>
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setApprovalMode('instant')}
                      className={`px-4 py-2 text-sm font-medium rounded-r-md border transition-colors ${
                        approvalMode === 'instant'
                          ? 'bg-primary text-white border-primary'
                          : 'bg-surface text-secondary-700 border-secondary-300 hover:bg-secondary-50'
                      }`}
                    >
                      <span className="flex items-center space-x-1">
                        <Icon name="Zap" size={16} />
                        <span>Instant Mode</span>
                      </span>
                    </button>
                  </div>
                </div>

                <div className="text-right mr-4">
                  <p className="text-sm text-text-secondary">Progress</p>
                  <p className="text-lg font-semibold text-primary">
                    {approvedIds.size} approved • {rejectedIds.size} rejected • {flaggedIds.size} flagged
                  </p>
                </div>

                <button
                  onClick={() => setStep('upload')}
                  className="px-4 py-2 text-secondary-700 bg-surface border border-secondary-300 rounded-md hover:bg-secondary-50"
                >
                  Cancel
                </button>

                {approvalMode === 'batch' ? (
                  <button
                    onClick={handleBulkSubmit}
                    disabled={processing || approvedIds.size === 0}
                    className="px-6 py-2 bg-success text-white rounded-md hover:bg-success-dark disabled:opacity-50 flex items-center space-x-2"
                  >
                    {processing && <Icon name="Loader2" size={16} className="animate-spin" />}
                    <span>Submit {approvedIds.size} Approvals</span>
                  </button>
                ) : (
                  <button
                    onClick={handleFinishReview}
                    disabled={reviewedCount === 0}
                    className="px-6 py-2 bg-primary text-white rounded-md hover:bg-primary-dark disabled:opacity-50 flex items-center space-x-2"
                  >
                    <Icon name="CheckCircle" size={16} />
                    <span>Finish Review</span>
                  </button>
                )}
              </div>
            </div>

            {/* Progress Bar */}
            <div className="w-full bg-secondary-200 rounded-full h-2 mb-3">
              <div
                className="bg-primary h-2 rounded-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>

            {/* Mode Info Banner */}
            <div className={`rounded-md p-3 text-sm ${
              approvalMode === 'instant'
                ? 'bg-warning-50 border border-warning-200'
                : 'bg-info-50 border border-info-200'
            }`}>
              <div className="flex items-start space-x-2">
                <Icon
                  name={approvalMode === 'instant' ? 'Zap' : 'Package'}
                  size={16}
                  className={approvalMode === 'instant' ? 'text-warning-600 mt-0.5' : 'text-info-600 mt-0.5'}
                />
                <p className={approvalMode === 'instant' ? 'text-warning-800' : 'text-info-800'}>
                  {approvalMode === 'instant' ? (
                    <span><strong>Instant Mode:</strong> Each approval is saved immediately to database and recruiters are notified right away. No need to click "Submit" button.</span>
                  ) : (
                    <span><strong>Batch Mode:</strong> Mark all biodata first, then submit all approvals at once using the "Submit" button. Notifications sent after batch submission.</span>
                  )}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Split Screen Content */}
        <div className="max-w-7xl mx-auto p-6">
          <div className="grid grid-cols-2 gap-6">
            {/* Left Panel - Excel Reference Data */}
            <div className="card p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-text-primary flex items-center space-x-2">
                  <Icon name="FileSpreadsheet" size={20} className="text-success" />
                  <span>Excel Reference</span>
                </h2>
                {currentRecord.match_status.test_pass && (
                  <span className="px-3 py-1 bg-success-100 text-success-800 text-sm font-medium rounded-full">
                    TEST: PASS
                  </span>
                )}
              </div>

              <div className="space-y-3">
                <div>
                  <label className="text-xs text-text-tertiary">Master SI No.</label>
                  <p className="text-sm font-medium text-text-primary">
                    {currentRecord.excel_data.master_si_no || '-'}
                  </p>
                </div>

                <div>
                  <label className="text-xs text-text-tertiary">Name</label>
                  <p className="text-sm font-medium text-text-primary">
                    {currentRecord.excel_data.name}
                  </p>
                </div>

                <div>
                  <label className="text-xs text-text-tertiary">Passport Number</label>
                  <p className="text-sm font-medium text-text-primary">
                    {currentRecord.excel_data.passport}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-text-tertiary">DOB</label>
                    <p className="text-sm font-medium text-text-primary">
                      {currentRecord.excel_data.dob || '-'}
                    </p>
                  </div>
                  <div>
                    <label className="text-xs text-text-tertiary">Age</label>
                    <p className="text-sm font-medium text-text-primary">
                      {currentRecord.excel_data.age || '-'}
                    </p>
                  </div>
                </div>

                {currentRecord.excel_data.remarks && (
                  <div>
                    <label className="text-xs text-text-tertiary">Remarks</label>
                    <p className="text-sm text-text-secondary italic">
                      {currentRecord.excel_data.remarks}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Right Panel - System Biodata */}
            <div className="card p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-text-primary flex items-center space-x-2">
                  <Icon name="Database" size={20} className="text-primary" />
                  <span>System Biodata</span>
                </h2>
                <span className="px-3 py-1 bg-primary-100 text-primary-800 text-sm font-medium rounded-full">
                  {currentRecord.system_data.worker_id}
                </span>
              </div>

              <div className="space-y-3">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <label className="text-xs text-text-tertiary">Name</label>
                    <p className="text-sm font-medium text-text-primary">
                      {currentRecord.system_data.full_name}
                    </p>
                  </div>
                  {currentRecord.match_status.name_match ? (
                    <Icon name="CheckCircle" size={18} className="text-success" />
                  ) : (
                    <Icon name="AlertTriangle" size={18} className="text-warning" />
                  )}
                </div>

                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <label className="text-xs text-text-tertiary">Passport Number</label>
                    <p className="text-sm font-medium text-text-primary">
                      {currentRecord.system_data.passport_no}
                    </p>
                  </div>
                  <Icon name="CheckCircle" size={18} className="text-success" />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-text-tertiary">DOB</label>
                    <div className="flex items-center space-x-2">
                      <p className="text-sm font-medium text-text-primary">
                        {currentRecord.system_data.date_of_birth
                          ? new Date(currentRecord.system_data.date_of_birth).toLocaleDateString()
                          : '-'}
                      </p>
                      {currentRecord.match_status.dob_match ? (
                        <Icon name="CheckCircle" size={16} className="text-success" />
                      ) : (
                        <Icon name="AlertTriangle" size={16} className="text-warning" />
                      )}
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-text-tertiary">Age</label>
                    <p className="text-sm font-medium text-text-primary">
                      {currentRecord.system_data.age || '-'}
                    </p>
                  </div>
                </div>

                <div>
                  <label className="text-xs text-text-tertiary">Medical Expiry</label>
                  <p className="text-sm font-medium text-text-primary">
                    {currentRecord.system_data.medical_expiry_date
                      ? new Date(currentRecord.system_data.medical_expiry_date).toLocaleDateString()
                      : '-'}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-text-tertiary">VDR Type</label>
                    <p className="text-sm font-medium text-text-primary">
                      {currentRecord.system_data.vdr_type || '-'}
                    </p>
                  </div>
                  <div>
                    <label className="text-xs text-text-tertiary">Nationality</label>
                    <p className="text-sm font-medium text-text-primary">
                      {currentRecord.system_data.nationality || '-'}
                    </p>
                  </div>
                </div>

                <div>
                  <label className="text-xs text-text-tertiary">Recruiter</label>
                  <p className="text-sm font-medium text-text-primary">
                    {currentRecord.system_data.recruiter?.name || '-'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Documents Section - Full Width */}
          <div className="card p-6 mt-6">
            <h3 className="text-lg font-semibold text-text-primary mb-4 flex items-center space-x-2">
              <Icon name="FileText" size={20} />
              <span>Documents & Photos</span>
              <span className="text-sm font-normal text-text-secondary">
                ({currentRecord.documents.length + currentRecord.photos.length} files)
              </span>
            </h3>

            <div className="grid grid-cols-3 gap-4">
              {/* Documents */}
              {currentRecord.documents.map((doc, idx) => (
                <button
                  key={`doc-${idx}`}
                  onClick={() => viewDocument(doc)}
                  className="flex items-center space-x-3 p-4 border border-secondary-200 rounded-lg hover:bg-secondary-50 transition-colors text-left"
                >
                  <div className="p-2 bg-primary-100 rounded">
                    <Icon name="FileText" size={20} className="text-primary-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-text-primary truncate">
                      {doc.type || 'Document'}
                    </p>
                    <p className="text-xs text-text-tertiary truncate">
                      {doc.file_name}
                    </p>
                  </div>
                  <Icon name="Eye" size={16} className="text-secondary-400" />
                </button>
              ))}

              {/* Photos */}
              {currentRecord.photos.map((photo, idx) => (
                <button
                  key={`photo-${idx}`}
                  onClick={() => viewDocument(photo)}
                  className="flex items-center space-x-3 p-4 border border-secondary-200 rounded-lg hover:bg-secondary-50 transition-colors text-left"
                >
                  <div className="p-2 bg-success-100 rounded">
                    <Icon name="Image" size={20} className="text-success-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-text-primary truncate">
                      {photo.type || 'Photo'}
                      {photo.is_primary && (
                        <span className="ml-2 text-xs text-success-600">(Primary)</span>
                      )}
                    </p>
                    <p className="text-xs text-text-tertiary truncate">
                      {photo.file_name}
                    </p>
                  </div>
                  <Icon name="Eye" size={16} className="text-secondary-400" />
                </button>
              ))}

              {currentRecord.documents.length === 0 && currentRecord.photos.length === 0 && (
                <div className="col-span-3 text-center py-8 text-text-tertiary">
                  <Icon name="AlertCircle" size={32} className="mx-auto mb-2 opacity-50" />
                  <p>No documents or photos uploaded</p>
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-between mt-6">
            <button
              onClick={goToPrevious}
              disabled={currentIndex === 0}
              className="flex items-center space-x-2 px-4 py-2 text-secondary-700 bg-surface border border-secondary-300 rounded-md hover:bg-secondary-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Icon name="ChevronLeft" size={20} />
              <span>Previous</span>
            </button>

            <div className="flex items-center space-x-3">
              <button
                onClick={handleReject}
                className="flex items-center space-x-2 px-6 py-3 bg-error text-white rounded-md hover:bg-error-dark transition-colors"
              >
                <Icon name="X" size={20} />
                <span>Reject (R)</span>
              </button>

              <button
                onClick={handleFlag}
                className="flex items-center space-x-2 px-6 py-3 bg-warning text-white rounded-md hover:bg-warning-dark transition-colors"
              >
                <Icon name="Flag" size={20} />
                <span>Flag for Review (F)</span>
              </button>

              <button
                onClick={handleApprove}
                className="flex items-center space-x-2 px-6 py-3 bg-success text-white rounded-md hover:bg-success-dark transition-colors"
              >
                <Icon name="Check" size={20} />
                <span>Approve (A)</span>
              </button>
            </div>

            <button
              onClick={goToNext}
              disabled={currentIndex >= totalRecords - 1}
              className="flex items-center space-x-2 px-4 py-2 text-secondary-700 bg-surface border border-secondary-300 rounded-md hover:bg-secondary-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span>Next</span>
              <Icon name="ChevronRight" size={20} />
            </button>
          </div>

          {/* Keyboard Shortcuts Help */}
          <div className="bg-secondary-50 border border-secondary-200 rounded-lg p-4 mt-6">
            <p className="text-sm font-medium text-text-secondary mb-2">Keyboard Shortcuts:</p>
            <div className="grid grid-cols-5 gap-4 text-sm text-text-tertiary">
              <div><kbd className="px-2 py-1 bg-surface rounded border">→</kbd> Next</div>
              <div><kbd className="px-2 py-1 bg-surface rounded border">←</kbd> Previous</div>
              <div><kbd className="px-2 py-1 bg-surface rounded border">A</kbd> Approve</div>
              <div><kbd className="px-2 py-1 bg-surface rounded border">R</kbd> Reject</div>
              <div><kbd className="px-2 py-1 bg-surface rounded border">F</kbd> Flag</div>
            </div>
          </div>
        </div>

        {/* Document Viewer Side Panel */}
        {showDocumentPanel && selectedDocument && (
          <div className="fixed inset-y-0 right-0 w-1/2 bg-surface border-l border-secondary-200 shadow-2xl z-50 overflow-y-auto">
            <div className="sticky top-0 bg-surface border-b border-secondary-200 p-4 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-text-primary">
                  {selectedDocument.type || 'Document'}
                </h3>
                <p className="text-sm text-text-secondary">{selectedDocument.file_name}</p>
              </div>
              <button
                onClick={() => setShowDocumentPanel(false)}
                className="p-2 hover:bg-secondary-100 rounded-md transition-colors"
              >
                <Icon name="X" size={24} />
              </button>
            </div>

            <div className="p-6">
              <div className="bg-secondary-100 rounded-lg p-4 min-h-[600px] flex items-center justify-center">
                {/* Document preview would go here - could use iframe for PDFs or img for images */}
                <div className="text-center text-text-tertiary">
                  <Icon name="FileText" size={64} className="mx-auto mb-4 opacity-50" />
                  <p className="mb-4">Document Preview</p>
                  <p className="text-sm">File: {selectedDocument.file_name}</p>
                  {selectedDocument.file_path && (
                    <a
                      href={`${API_BASE_URL}${selectedDocument.file_path}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center space-x-2 mt-4 px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark"
                    >
                      <Icon name="ExternalLink" size={16} />
                      <span>Open in New Tab</span>
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Render completion screen
  if (step === 'complete') {
    return (
      <div className="min-h-screen bg-background p-8">
        <Notifier />
        <div className="max-w-2xl mx-auto">
          <div className="card p-8 text-center">
            <div className="w-20 h-20 bg-success-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Icon name="CheckCircle" size={48} className="text-success" />
            </div>

            <h1 className="text-3xl font-bold text-text-primary mb-4">
              Bulk Verification Complete!
            </h1>

            <div className="bg-secondary-50 rounded-lg p-6 mb-6">
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <p className="text-3xl font-bold text-success">{approvedIds.size}</p>
                  <p className="text-sm text-text-secondary">Approved</p>
                </div>
                <div>
                  <p className="text-3xl font-bold text-error">{rejectedIds.size}</p>
                  <p className="text-sm text-text-secondary">Rejected</p>
                </div>
                <div>
                  <p className="text-3xl font-bold text-warning">{flaggedIds.size}</p>
                  <p className="text-sm text-text-secondary">Flagged</p>
                </div>
              </div>
            </div>

            <div className={`rounded-md p-4 mb-6 ${
              approvalMode === 'instant'
                ? 'bg-success-50 border border-success-200'
                : 'bg-info-50 border border-info-200'
            }`}>
              <div className="flex items-start space-x-3">
                <Icon
                  name={approvalMode === 'instant' ? 'Zap' : 'CheckCircle'}
                  size={20}
                  className={approvalMode === 'instant' ? 'text-success-600 mt-0.5' : 'text-info-600 mt-0.5'}
                />
                <p className={`text-sm ${approvalMode === 'instant' ? 'text-success-800' : 'text-info-800'}`}>
                  {approvalMode === 'instant' ? (
                    <span>All approved biodata have been saved to the database and recruiters have been notified in real-time.</span>
                  ) : (
                    <span>All {approvedIds.size} approved biodata have been submitted successfully. Recruiters have been notified.</span>
                  )}
                </p>
              </div>
            </div>

            <div className="flex items-center justify-center space-x-3">
              <button
                onClick={() => {
                  setStep('upload');
                  setFile(null);
                  setVerificationData(null);
                  setCurrentIndex(0);
                  setReviewedIds(new Set());
                  setApprovedIds(new Set());
                  setRejectedIds(new Set());
                  setFlaggedIds(new Set());
                }}
                className="px-6 py-2 bg-primary text-white rounded-md hover:bg-primary-dark"
              >
                Start New Verification
              </button>

              <button
                onClick={() => window.location.href = '/clab-biodata-approved'}
                className="px-6 py-2 text-secondary-700 bg-surface border border-secondary-300 rounded-md hover:bg-secondary-50"
              >
                View Approved Biodata
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default ClabBulkVerification;
